import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { LiveScanner } from "../../components/LiveScanner";
import { ProductImage } from "../../components/ProductImage";
import { Search, Plus, AlertTriangle, X, Check, Loader2, Info, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import PhotoCaptureField from "../../components/PhotoCaptureField";
import SmartCaptureBlock from "../../components/SmartCaptureBlock";
import { extractProductFromImage } from "../../lib/extractProduct";
import {
  findLocalProductByCode,
  searchLocalProducts,
  enqueueOutbox,
  isOnline,
} from "../../lib/offlineStore";
import { downloadCatalog } from "../../lib/catalogSync";
import syncRunner from "../../lib/syncRunner";
import { scanContextKey, sharedCatalogDiscovery, sharedCatalogSearchPage, unownedSharedProducts } from "../../lib/sharedCatalogDiscovery";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const DEFAULT_FORM = { quantity: 1, expiration_date: todayPlus(7), batch_code: "" };

export default function ScanPage({ operationalContext = null }) {
  const { user } = useAuth();
  const deptId = operationalContext?.departmentId || user?.department_id;
  const lookupStoreId = operationalContext?.storeId || user?.store_id || undefined;
  const isEmployee = user?.role === "employee";
  const canAttachSharedProduct = ["admin", "manager"].includes(user?.role);
  const scopeKey = scanContextKey(user, operationalContext);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const lookupGeneration = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; lookupGeneration.current += 1; };
  }, []);
  const beginLookup = useCallback(() => ({ scope: scopeRef.current, generation: ++lookupGeneration.current }), []);
  const isCurrentLookup = useCallback((ticket) => mountedRef.current
    && ticket.scope === scopeRef.current && ticket.generation === lookupGeneration.current, []);

  // Lightweight {id -> name} map of departments — refreshed with the context
  // and used so the cross-department guard can name the *wrong* department
  // ("This item belongs to Cold Cuts, not Cheese") on a local-cache hit,
  // where the backend has no chance to enrich the response.
  const [deptNameMap, setDeptNameMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    setDeptNameMap({});
    api.get("/departments").then(({ data }) => {
      if (cancelled || !Array.isArray(data)) return;
      const map = {};
      for (const d of data) if (d && d.id) map[d.id] = d.name;
      setDeptNameMap(map);
    }).catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [scopeKey]);

  // Returns true when the loaded product is in a different department
  // than the signed-in employee. Always returns false for managers and
  // admins — they aren't department-scoped.
  const isCrossDept = useCallback((p) => {
    if (!isEmployee) return false;
    return !!(p && p.department_id && deptId && p.department_id !== deptId);
  }, [isEmployee, deptId]);

  const crossDeptMessage = useCallback((p) => {
    const otherName = deptLabel(deptNameMap[p?.department_id]) || "another department";
    const ownName = deptLabel(user?.department_name) || "your department";
    return `This item belongs to ${otherName}, not ${ownName}.`;
  }, [deptNameMap, user]);

  const operationalContextError = useCallback((p) => {
    if (!p || !operationalContext) return null;
    if (
      operationalContext.storeId
      && p.store_id
      && p.store_id !== operationalContext.storeId
    ) {
      return "This product is not available in the selected store.";
    }
    if (
      operationalContext.departmentId
      && p.department_id !== operationalContext.departmentId
    ) {
      return "This product is not in the selected department.";
    }
    return null;
  }, [operationalContext]);

  // current product loaded in the bottom panel
  const [current, setCurrent] = useState(null); // product object | null
  const [scannedBarcode, setScannedBarcode] = useState(null); // raw barcode used for this load (for shelf-life learning)
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shelfLifeSuggestion, setShelfLifeSuggestion] = useState(null); // { suggested_days, suggested_date } | null
  const [quantitySuggestion, setQuantitySuggestion] = useState(null); // { suggested_quantity } | null

  // confirmation when scanning a different barcode while panel is dirty
  const [pendingProduct, setPendingProduct] = useState(null);
  const [sharedDiscovery, setSharedDiscovery] = useState(null);
  const [attachingShared, setAttachingShared] = useState(false);
  const attachAttemptRef = useRef(null);

  // unknown barcode → product request
  const [requestSheet, setRequestSheet] = useState(null);
  const [recognizedRequest, setRecognizedRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ barcode: "", name_guess: "", brand: "", notes: "" });
  const [requestPhotos, setRequestPhotos] = useState([]);
  // Track how the values were collected for audit trail.
  const [extractionSource, setExtractionSource] = useState(null); // 'barcode_scan' | 'photo_ocr' | 'mixed' | null

  // text search panel (separate; opens a list)
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchState, setSearchState] = useState(null);
  const [searchRetry, setSearchRetry] = useState(0);
  const [searchSelection, setSearchSelection] = useState(null);
  const searchSessionRef = useRef(0);
  const moreSharedAttemptRef = useRef(null);
  const searchContextRef = useRef(null);
  const searchKey = JSON.stringify([scopeKey, query, searchOpen, searchSessionRef.current, searchRetry]);
  searchContextRef.current = searchKey;
  const isCurrentSearch = useCallback((ticket) => mountedRef.current
    && ticket.key === searchContextRef.current && ticket.session === searchSessionRef.current, []);
  const activeSearch = searchState?.key === searchKey ? searchState : null;
  const results = activeSearch?.owned || [];
  const sharedResults = unownedSharedProducts(activeSearch?.shared || [], results);
  const hasQuery = !!query.trim();
  const searching = hasQuery && (!activeSearch || activeSearch.ownedStatus === "loading"
    || activeSearch.sharedStatus === "loading");
  const selectingSearch = searchSelection?.key === searchKey;
  const closeSearch = useCallback(() => {
    searchSessionRef.current += 1;
    setSearchOpen(false);
    setQuery("");
    setSearchState(null);
    setSearchSelection(null);
  }, []);

  useEffect(() => {
    lookupGeneration.current += 1;
    setSharedDiscovery(null);
    setAttachingShared(false);
    attachAttemptRef.current = null;
    setCurrent(null);
    setPendingProduct(null);
    setScannedBarcode(null);
    setForm(DEFAULT_FORM);
    setShelfLifeSuggestion(null);
    setQuantitySuggestion(null);
    setRequestSheet(null);
    setRecognizedRequest(false);
    setRequestPhotos([]);
    setRequestForm({ barcode: "", name_guess: "", brand: "", notes: "" });
    setExtractionSource(null);
    setSearchOpen(false);
    searchSessionRef.current += 1;
    setQuery("");
    setSearchState(null);
    setSearchSelection(null);
    setLoading(false);
    setSubmitting(false);
  }, [scopeKey]);

  const offerSharedProduct = useCallback((data, barcode, ticket) => {
    const discovery = sharedCatalogDiscovery(data);
    if (!discovery) return false;
    if (canAttachSharedProduct) {
      setSharedDiscovery({ ...discovery, barcode, ticket });
    } else {
      setRecognizedRequest(true);
      setRequestSheet(barcode);
      setRequestForm({ barcode, name_guess: discovery.product.name, brand: discovery.product.brand || "", notes: "" });
      setRequestPhotos([]);
      setExtractionSource("barcode_scan");
    }
    return true;
  }, [canAttachSharedProduct]);

  // iPhone camera-access info banner (dismissible, persisted)
  const [showCameraBanner, setShowCameraBanner] = useState(() => {
    try { return localStorage.getItem("scanner_banner_hidden") !== "1"; } catch { return true; }
  });
  const dismissCameraBanner = () => {
    try { localStorage.setItem("scanner_banner_hidden", "1"); } catch (_) {}
    setShowCameraBanner(false);
  };

  // ---------------------------------------------------------------
  // Android / browser system Back handling for inline scan overlays.
  //
  // The Search panel and the Request-new-product sheet are inline
  // overlays — they don't change the URL. Without intervention, the
  // browser back button (or Android system Back) would leave /scan
  // entirely while an overlay is open, which surprises operators.
  //
  // Approach: while ANY overlay is open we push a single sentinel
  // history entry. System Back consumes that entry → popstate fires →
  // we close the overlay and stay on /scan. When an overlay is closed
  // via the on-screen "Back to Scanner" button (programmatic), we pop
  // the sentinel ourselves so the next Back behaves normally.
  //
  // The same sentinel covers both overlays — they're never both open
  // in steady state (the "Request new product" CTA closes Search
  // before opening the request sheet), so a single back-press cleanly
  // returns to the scanner regardless of which overlay was visible.
  // ---------------------------------------------------------------
  const overlayPushedRef = useRef(false);
  const anyOverlayOpen = searchOpen || requestSheet !== null || sharedDiscovery !== null;

  useEffect(() => {
    if (anyOverlayOpen && !overlayPushedRef.current) {
      overlayPushedRef.current = true;
      try {
        window.history.pushState({ shelflifeOverlay: true }, "");
      } catch (_) {
        // Some browsers throw on too-frequent pushState; if it fails
        // we silently fall back to the unlabeled close icon flow.
        overlayPushedRef.current = false;
      }
    } else if (!anyOverlayOpen && overlayPushedRef.current) {
      // Programmatic close (e.g. clicked Back-to-Scanner). Pop the
      // sentinel so subsequent Back exits /scan normally. Only call
      // history.back() if our sentinel is still the topmost entry —
      // otherwise popstate already cleaned it up.
      overlayPushedRef.current = false;
      try {
        if (window.history.state && window.history.state.shelflifeOverlay) {
          window.history.back();
        }
      } catch (_) { /* private mode / detached frame */ }
    }
  }, [anyOverlayOpen]);

  useEffect(() => {
    const onPopState = () => {
      // System Back consumed our sentinel. Close every overlay so the
      // user stays on /scan with the camera ready.
      if (overlayPushedRef.current) {
        overlayPushedRef.current = false;
        setRequestSheet(null);
        setSharedDiscovery(null);
        setAttachingShared(false);
        attachAttemptRef.current = null;
        lookupGeneration.current += 1;
        closeSearch();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [closeSearch]);

  // dirty = has the user touched any field?
  const isDirty = useMemo(() => {
    if (!current) return false;
    return (
      Number(form.quantity) !== DEFAULT_FORM.quantity ||
      form.expiration_date !== DEFAULT_FORM.expiration_date ||
      (form.batch_code || "").length > 0
    );
  }, [current, form]);

  const applySuggestionsForBarcode = useCallback(async (barcode, ticket = { scope: scopeRef.current, generation: lookupGeneration.current }) => {
    if (!barcode) return;
    try {
      const sug = await api.get("/shelf-life/suggest", { params: { barcode } });
      if (!isCurrentLookup(ticket)) return;
      const patch = {};
      if (sug.data?.is_scale_code && sug.data?.suggested_date) {
        patch.expiration_date = sug.data.suggested_date;
        setShelfLifeSuggestion({
          suggested_days: sug.data.suggested_days,
          suggested_date: sug.data.suggested_date,
        });
      }
      if (sug.data?.suggested_quantity && sug.data.suggested_quantity > 0) {
        patch.quantity = sug.data.suggested_quantity;
        setQuantitySuggestion({ suggested_quantity: sug.data.suggested_quantity });
      }
      if (Object.keys(patch).length) {
        setForm((f) => ({ ...f, ...patch }));
      }
    } catch (_) { /* suggestion is best-effort */ }
  }, [isCurrentLookup]);

  const loadProduct = useCallback(async (barcode) => {
    if (scopeKey !== scopeRef.current || !mountedRef.current) return;
    const ticket = beginLookup();
    setLoading(true);
    // Wrap the entire flow in try/finally so that ANY unexpected failure
    // (IDB throws, the API base URL is unreachable, the suggestion
    // endpoint times out, ...) still releases the "Loading product..."
    // spinner. Without this outer finally a single thrown IDB error
    // would leave the page stuck forever — that's the bug that surfaced
    // on iPhone after the offline MVP shipped.
    try {
      const online = isOnline();
      // Cached approval can become stale. Only use the device catalog when
      // explicitly offline; online reads must honor the current server view.
      let local = null;
      if (!online && user && user.id) {
        try { local = await findLocalProductByCode(user.id, barcode); }
        catch (_) { local = null; /* swallowed: helper is already defensive */ }
      }
      if (!isCurrentLookup(ticket)) return;
      if (local) {
        // Department scope guard — local-cache hit may pre-date the
        // server-side scope tightening (or the catalog was refreshed
        // while an employee was reassigned). Refuse to load and never
        // let the user mark a foreign-dept product as in stock.
        if (isCrossDept(local)) {
          toast.error(crossDeptMessage(local));
          return;
        }
        const contextError = operationalContextError(local);
        if (contextError) {
          toast.error(contextError);
          return;
        }
        setCurrent(local);
        setScannedBarcode(barcode);
        setForm(DEFAULT_FORM);
        setShelfLifeSuggestion(null);
        setQuantitySuggestion(null);
        // Suggestions are best-effort (and online-only).
        applySuggestionsForBarcode(barcode, ticket).catch(() => {});
        toast.success(`Loaded: ${local.name}`);
        return;
      }
      // 2) Offline + not in local catalog → friendly message, no backend call.
      if (!online) {
        toast.error("Product not found on this device. Connect to internet or refresh catalog.");
        return;
      }
      // 3) Online → authoritative backend lookup, without a cache fallback.
      try {
        const { data } = await api.get("/products/lookup", { params: { barcode, store_id: lookupStoreId } });
        if (!isCurrentLookup(ticket)) return;
        if (offerSharedProduct(data, barcode, ticket)) return;
        if (data?.source === "shared_catalog" || data?.requires_catalog_add || data?.shared_product) {
          toast.error("Could not verify the shared product. Scan it again.");
          return;
        }
        const product = data && data.product;
        if (!product) {
          toast.error("Unexpected response from server");
          return;
        }
        const contextError = operationalContextError(product);
        if (contextError) {
          toast.error(contextError);
          return;
        }
        setCurrent(product);
        setScannedBarcode(barcode);
        setForm(DEFAULT_FORM);
        setShelfLifeSuggestion(null);
        setQuantitySuggestion(null);
        applySuggestionsForBarcode(barcode, ticket).catch(() => {});
        toast.success(`Loaded: ${product.name}`);
      } catch (e) {
        if (!isCurrentLookup(ticket)) return;
        const status = e?.response?.status;
        if (status === 404) {
          toast.warning("Unknown barcode — submit a product request");
          setRequestSheet(barcode);
          setRecognizedRequest(false);
          setRequestForm({ barcode, name_guess: "", brand: "", notes: "" });
          setExtractionSource("barcode_scan");
        } else if (status === 403) {
          // Server-side cross-department guard. Surface the dept name
          // from the API detail directly.
          toast.error(formatApiErrorDetail(e) || "This item is not in your department.");
        } else if (!e?.response) {
          // Genuinely offline mid-request → mirror the offline message.
          toast.error("Product not found on this device. Connect to internet or refresh catalog.");
        } else {
          toast.error(formatApiErrorDetail(e));
        }
      }
    } catch (e) {
      if (!isCurrentLookup(ticket)) return;
      // Last-resort safety net — must NEVER leave loading=true.
      if (typeof console !== "undefined") console.error("loadProduct crashed:", e);
      toast.error("Could not load product (please try again)");
    } finally {
      if (isCurrentLookup(ticket)) setLoading(false);
    }
  }, [applySuggestionsForBarcode, beginLookup, crossDeptMessage, isCrossDept, isCurrentLookup, offerSharedProduct, operationalContextError, lookupStoreId, scopeKey, user]);

  // === scanner: handle a new barcode ============================
  const onScanned = useCallback(async (barcode) => {
    if (scopeKey !== scopeRef.current || !mountedRef.current) return;
    if (!barcode || sharedDiscovery || attachingShared || requestSheet !== null || submitting) return;
    try {
      // same product already loaded → ignore
      if (current && current.barcode === barcode) return;

      // a product is loaded with edits → ask before replacing
      if (current && isDirty) {
        const ticket = beginLookup();
        const online = isOnline();
        let local = null;
        if (!online && user && user.id) {
          try { local = await findLocalProductByCode(user.id, barcode); } catch (_) { local = null; }
        }
        if (!isCurrentLookup(ticket)) return;
        if (local) { setPendingProduct(local); return; }
        if (!online) {
          toast.warning("Product not on this device (save current item first)");
          return;
        }
        try {
          const { data } = await api.get("/products/lookup", { params: { barcode, store_id: lookupStoreId } });
          if (!isCurrentLookup(ticket)) return;
          if (offerSharedProduct(data, barcode, ticket)) return;
          if (data?.source === "shared_catalog" || data?.requires_catalog_add || data?.shared_product) {
            toast.error("Could not verify the shared product. Scan it again.");
            return;
          }
          if (data && data.product) setPendingProduct(data.product);
        } catch (e) {
          if (!isCurrentLookup(ticket)) return;
          if (e?.response?.status === 404) {
            toast.warning("Unknown barcode (please save current first)");
          }
          // Any other error → silently ignore; scanner will retry.
        }
        return;
      }
      // safe to replace
      await loadProduct(barcode);
    } catch (e) {
      // Belt-and-suspenders: scanner callbacks must never throw —
      // otherwise the LiveScanner shows a generic crash overlay.
      if (typeof console !== "undefined") console.error("onScanned crashed:", e);
    }
  }, [attachingShared, beginLookup, current, isCurrentLookup, isDirty, loadProduct, lookupStoreId, offerSharedProduct, requestSheet, scopeKey, sharedDiscovery, submitting, user]);

  const dismissSharedProduct = () => {
    lookupGeneration.current += 1;
    setSharedDiscovery(null);
    setAttachingShared(false);
    attachAttemptRef.current = null;
  };

  const attachSharedProduct = async () => {
    const discovery = sharedDiscovery;
    if (!discovery || attachAttemptRef.current || attachingShared || !canAttachSharedProduct || !isCurrentLookup(discovery.ticket)) return;
    const storeId = operationalContext ? operationalContext.storeId : lookupStoreId;
    const departmentId = operationalContext ? operationalContext.departmentId : deptId;
    if (!storeId || !departmentId) {
      toast.error("Select a store and department before adding this product to the catalog.");
      return;
    }
    if (!isOnline()) {
      toast.error("Connect to the internet to add this product to the catalog.");
      return;
    }
    attachAttemptRef.current = discovery.ticket;
    setAttachingShared(true);
    try {
      const { data } = await api.post("/products/shared-catalog", {
        barcode: discovery.barcode,
        product_id: discovery.product.id,
        expected_digest: discovery.digest,
        department_id: departmentId,
        store_id: storeId,
      }, { headers: { "X-ShelfLife-Outbox-User": user.id } });
      if (!isCurrentLookup(discovery.ticket)) return;
      const product = data?.product;
      if (!product?.id || product.id !== discovery.product.id || product.department_id !== departmentId
          || (product.store_id && product.store_id !== storeId) || data.requires_catalog_add) {
        toast.error("Could not confirm this product in the selected catalog. Scan it again.");
        return;
      }
      setSharedDiscovery(null);
      if (current && isDirty) {
        setPendingProduct(product);
      } else {
        setCurrent(product);
        setScannedBarcode(discovery.barcode);
        setForm(DEFAULT_FORM);
        setShelfLifeSuggestion(null);
        setQuantitySuggestion(null);
        applySuggestionsForBarcode(discovery.barcode, discovery.ticket).catch(() => {});
      }
      toast.success(`Added ${product.name} to the store catalog`);
    } catch (error) {
      if (!isCurrentLookup(discovery.ticket)) return;
      toast.error(formatApiErrorDetail(error));
      // A failed/revoked review needs a fresh scan; it is never queued offline.
      setSharedDiscovery(null);
    } finally {
      if (attachAttemptRef.current === discovery.ticket) attachAttemptRef.current = null;
      if (isCurrentLookup(discovery.ticket)) setAttachingShared(false);
    }
  };

  const acceptPending = async () => {
    if (!pendingProduct) return;
    const p = pendingProduct;
    if (isCrossDept(p)) {
      toast.error(crossDeptMessage(p));
      setPendingProduct(null);
      return;
    }
    const contextError = operationalContextError(p);
    if (contextError) {
      toast.error(contextError);
      setPendingProduct(null);
      return;
    }
    setCurrent(p);
    setScannedBarcode(p.barcode || null);
    setShelfLifeSuggestion(null);
    setQuantitySuggestion(null);
    setForm(DEFAULT_FORM);
    setPendingProduct(null);
    if (p.barcode) {
      await applySuggestionsForBarcode(p.barcode);
    }
    toast.success(`Switched to: ${p.name}`);
  };
  const cancelPending = () => setPendingProduct(null);

  const submitAdd = async () => {
    if (!current) return;
    if (operationalContext && !operationalContext.storeId) {
      toast.error("Select a store before adding stock.");
      return;
    }
    // Defense-in-depth: block save the moment we detect a cross-dept
    // product in state. The server already enforces this with a 403,
    // but disabling the UX path eliminates the round-trip + error toast
    // and prevents the action ever entering the offline outbox.
    if (isCrossDept(current)) {
      toast.error(crossDeptMessage(current));
      return;
    }
    const contextError = operationalContextError(current);
    if (contextError) {
      toast.error(contextError);
      return;
    }
    setSubmitting(true);
    const learningKey = scannedBarcode || current.barcode || null;
    const body = {
      product_id: current.id,
      quantity: Number(form.quantity),
      expiration_date: form.expiration_date,
      batch_code: form.batch_code || null,
      scanned_barcode: learningKey,
      ...(operationalContext?.storeId ? { store_id: operationalContext.storeId } : {}),
      ...(current.department_id ? { department_id: current.department_id } : {}),
    };
    const enqueueAndAck = async () => {
      if (!user || !user.id) {
        toast.error("Sign in to queue items for sync");
        return;
      }
      try {
        await enqueueOutbox(user.id, { kind: "add_inventory", url: "/inventory", body });
        syncRunner.kick("scan-add");
        toast.success(`Queued: ${form.quantity} × ${current.name} (will sync when online)`);
        setCurrent(null);
        setScannedBarcode(null);
        setShelfLifeSuggestion(null);
        setQuantitySuggestion(null);
        setForm(DEFAULT_FORM);
      } catch (e) {
        // IDB write failed (quota, locked) — surface it instead of
        // silently dropping the user's action.
        toast.error("Could not queue offline (storage error)");
      }
    };
    try {
      if (!isOnline() && user && user.id) {
        await enqueueAndAck();
        return;
      }
      try {
        await api.post("/inventory", body);
        toast.success(`Added ${form.quantity} × ${current.name}`);
        setCurrent(null);
        setScannedBarcode(null);
        setShelfLifeSuggestion(null);
        setQuantitySuggestion(null);
        setForm(DEFAULT_FORM);
      } catch (e) {
        if (!e?.response && user && user.id) {
          await enqueueAndAck();
        } else {
          toast.error(formatApiErrorDetail(e));
        }
      }
    } catch (e) {
      if (typeof console !== "undefined") console.error("submitAdd crashed:", e);
      toast.error("Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  // === manual search ============================================
  useEffect(() => {
    const q = query.trim();
    if (!searchOpen || !q) return undefined;
    const ticket = { key: searchKey, session: searchSessionRef.current };
    let cancelled = false;
    const online = isOnline();
    const publish = (patch) => {
      if (!cancelled && isCurrentSearch(ticket)) {
        setSearchState((state) => state?.key === ticket.key ? { ...state, ...patch } : state);
      }
    };
    setSearchState({ key: searchKey, owned: [], shared: [], ownedStatus: "loading",
      sharedStatus: online ? "loading" : "offline", nextCursor: null });
    const timer = setTimeout(() => {
      const ownedSearch = async () => {
        try {
          const data = online
            ? (await api.get("/products", { params: { q, department_id: deptId, store_id: lookupStoreId } })).data
            : (user?.id ? await searchLocalProducts(user.id, q, 30) : []);
          if (!Array.isArray(data)) throw new Error("Invalid catalog response");
          publish({ owned: data, ownedStatus: "ready" });
        } catch (_) {
          publish({ owned: [], ownedStatus: "error" });
        }
      };
      const sharedSearch = async () => {
        if (!online) return;
        try {
          const { data } = await api.get("/products/shared-search", { params: { q, limit: 20 } });
          const page = sharedCatalogSearchPage(data);
          publish({ shared: page.items, nextCursor: page.nextCursor, sharedStatus: "ready" });
        } catch (_) {
          publish({ shared: [], sharedStatus: "error" });
        }
      };
      void Promise.all([ownedSearch(), sharedSearch()]);
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, searchOpen, searchKey, deptId, lookupStoreId, user?.id, isCurrentSearch]);

  const loadMoreShared = async () => {
    if (!activeSearch?.nextCursor || activeSearch.loadingMore || selectingSearch) return;
    const ticket = { key: searchKey, session: searchSessionRef.current };
    if (!isCurrentSearch(ticket) || (moreSharedAttemptRef.current && isCurrentSearch(moreSharedAttemptRef.current))) return;
    moreSharedAttemptRef.current = ticket;
    const cursor = activeSearch.nextCursor;
    setSearchState((state) => ({ ...state, loadingMore: true, moreError: false }));
    try {
      const { data } = await api.get("/products/shared-search", {
        params: { q: query.trim(), limit: 20, cursor },
      });
      const page = sharedCatalogSearchPage(data);
      if (page.nextCursor === cursor) throw new Error("Shared catalog search did not advance");
      if (!isCurrentSearch(ticket)) return;
      setSearchState((state) => ({ ...state, shared: [...state.shared, ...page.items],
        nextCursor: page.nextCursor, loadingMore: false }));
    } catch (_) {
      if (isCurrentSearch(ticket)) setSearchState((state) => ({ ...state, loadingMore: false, moreError: true }));
    } finally {
      if (moreSharedAttemptRef.current === ticket) moreSharedAttemptRef.current = null;
    }
  };

  const refreshCatalogManually = async () => {
    if (!user || !user.id) return;
    if (!isOnline()) { toast.error("Offline — connect first to refresh the catalog"); return; }
    toast.message("Refreshing catalog…");
    try {
      const res = await downloadCatalog(user, { force: true });
      if (res && Array.isArray(res.products)) {
        toast.success(`Catalog refreshed — ${res.products.length} products`);
      } else {
        toast.error("Catalog refresh failed");
      }
    } catch (e) {
      toast.error("Catalog refresh failed");
    }
  };

  const pickFromSearch = async (p) => {
    if (isCrossDept(p)) {
      // Belt-and-braces: the backend search is now dept-scoped server-side
      // for employees, but the local catalog may pre-date that fix, so we
      // still refuse the pick client-side.
      toast.error(crossDeptMessage(p));
      return;
    }
    const contextError = operationalContextError(p);
    if (contextError) {
      toast.error(contextError);
      return;
    }
    if (current && current.barcode === p.barcode) {
      closeSearch();
      return;
    }
    if (current && isDirty) {
      setPendingProduct(p);
      closeSearch();
      return;
    }
    setCurrent(p);
    // Use the product's stored barcode as the learning key for searched items.
    // The backend normalizes it to scale_code (scale items) or treats it as a
    // raw UPC, so the same lookup_key is reached regardless of entry path.
    setScannedBarcode(p.barcode || null);
    setForm(DEFAULT_FORM);
    setShelfLifeSuggestion(null);
    setQuantitySuggestion(null);
    closeSearch();
    if (p.barcode) {
      await applySuggestionsForBarcode(p.barcode);
    }
  };

  const pickSharedFromSearch = async (preview) => {
    const searchTicket = { key: searchKey, session: searchSessionRef.current };
    if (!isCurrentSearch(searchTicket) || selectingSearch || !isOnline()) return;
    const ticket = beginLookup();
    setSearchSelection({ key: searchKey, id: preview.id });
    try {
      // Search previews are not stock authorization. Recheck the reviewed
      // identity and current tenant membership before opening either flow.
      const { data } = await api.get("/products/lookup", { params: { barcode: preview.barcode, store_id: lookupStoreId } });
      if (!isCurrentSearch(searchTicket) || !isCurrentLookup(ticket)) return;
      if (offerSharedProduct(data, preview.barcode, ticket)) {
        closeSearch();
      } else if (data?.source === "shared_catalog" || data?.requires_catalog_add || data?.shared_product || !data?.product) {
        toast.error("Could not verify this shared product. Search again.");
      } else {
        await pickFromSearch(data.product);
      }
    } catch (error) {
      if (!isCurrentSearch(searchTicket) || !isCurrentLookup(ticket)) return;
      toast.error(error?.response?.status === 404
        ? "This shared product is no longer available. Search again."
        : "Could not verify this shared product. Try again when connected.");
    } finally {
      if (isCurrentSearch(searchTicket)) setSearchSelection(null);
    }
  };

  const submitRequest = async () => {
    const bc = (requestForm.barcode || "").trim();
    if (!bc) {
      toast.error("Barcode is required");
      return;
    }
    if (operationalContext && !operationalContext.storeId) {
      toast.error("Select a store before submitting a product request.");
      return;
    }
    if (operationalContext?.requireDepartmentForRequest && !operationalContext.departmentId) {
      toast.error("Select a department before submitting an unknown product request.");
      return;
    }
    setSubmitting(true);
    const body = {
      barcode: bc,
      name_guess: requestForm.name_guess || null,
      brand: requestForm.brand || null,
      notes: requestForm.notes || null,
      photos: requestPhotos.length ? requestPhotos : null,
      extraction_source: extractionSource || "manual",
      ...(operationalContext?.storeId ? { store_id: operationalContext.storeId } : {}),
      ...(deptId ? { department_id: deptId } : {}),
    };
    const resetForm = () => {
      setRequestSheet(null);
      setRequestForm({ barcode: "", name_guess: "", brand: "", notes: "" });
      setRequestPhotos([]);
      setExtractionSource(null);
    };
    const queueOffline = async (banner) => {
      if (!user?.id) return false;
      try {
        await enqueueOutbox(user.id, {
          kind: "product_request",
          url: "/product-requests",
          body,
          targetId: bc,
        });
        toast.success(banner);
        resetForm();
        try { syncRunner.kick("request-queue"); } catch (_) { /* noop */ }
        return true;
      } catch (_) {
        toast.error("Could not queue offline — please try again");
        return false;
      }
    };

    if (!isOnline()) {
      await queueOffline("Queued offline — will submit when online");
      setSubmitting(false);
      return;
    }
    try {
      await api.post("/product-requests", body);
      toast.success("Product request submitted");
      resetForm();
    } catch (e) {
      // Network mid-flight (no e.response) → queue rather than lose work.
      if (!e?.response) {
        await queueOffline("Queued — will submit when online");
      } else {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Smart-capture detected one or more fields. Update the form; bump the
  // extraction source so the audit trail reflects how data was sourced.
  const applyDetected = ({ barcode, product_name, brand, source }) => {
    setRequestForm((prev) => {
      const next = { ...prev };
      if (barcode) next.barcode = String(barcode);
      if (product_name) next.name_guess = product_name;
      if (brand) next.brand = brand;
      return next;
    });
    setExtractionSource((prev) => {
      if (!prev || prev === "manual") return source || "photo_ocr";
      if (prev === source) return prev;
      return "mixed";
    });
  };

  // pause scanner when any modal is on top, to avoid spurious detections
  const sharedContextReady = !!(operationalContext ? operationalContext.storeId : lookupStoreId)
    && !!(operationalContext ? operationalContext.departmentId : deptId);
  const scannerPaused = !!pendingProduct || requestSheet !== null || !!sharedDiscovery || searchOpen || submitting || attachingShared;

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] sm:h-[calc(100vh-5rem)]" data-testid="scan-page">
      {/* ---------- camera hint banner (dismissible, iOS-only situations) ---------- */}
      {showCameraBanner && (
        <div
          className="shrink-0 flex items-start gap-2 bg-shelf-attention-bg text-shelf-attention border-b border-shelf-attention-bd px-3 py-2"
          role="status"
          data-testid="camera-preview-warning"
        >
          <Info size={14} className="mt-[2px] shrink-0" />
          <p className="flex-1 text-[11px] sm:text-xs leading-snug font-mono">
            Camera not opening? On iPhone, open ShelfLife in Safari. On Android, allow camera access when prompted.
          </p>
          <button
            type="button"
            onClick={dismissCameraBanner}
            className="shrink-0 -mt-1 -mr-1 p-1 text-shelf-attention/80 hover:text-shelf-attention"
            aria-label="Dismiss camera tip"
            data-testid="camera-preview-warning-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* ---------- live camera (top half) ---------- */}
      <div className="relative h-[42vh] min-h-[260px] max-h-[420px] bg-black">
        <LiveScanner onDetected={onScanned} paused={scannerPaused} />
        {/* manual search shortcut — large touch target */}
        <button
          onClick={() => {
            lookupGeneration.current += 1;
            searchSessionRef.current += 1;
            setLoading(false);
            setSearchOpen(true);
          }}
          className="absolute top-3 left-3 h-9 px-3 bg-white/95 text-ink rounded-sm font-mono text-[11px] uppercase tracking-wider flex items-center gap-1.5 shadow-sm active:scale-[0.98] transition-transform"
          data-testid="open-search-button"
        >
          <Search size={14} strokeWidth={2.5} /> Search
        </button>
      </div>

      {/* ---------- bottom info panel ---------- */}
      <div className="flex-1 overflow-y-auto bg-paper px-4 pt-4 pb-2 border-t-2 border-ink" data-testid="scan-panel">
        {!current && !loading && (
          <div className="py-8 text-center" data-testid="scan-empty-state">
            <div className="data-sku mb-2">
              {operationalContext?.storeName || deptLabel(user?.department_name) || "Workspace"}
            </div>
            <div className="font-display font-black text-xl mb-1">Scan a product barcode</div>
            <div className="text-sm text-muted-foreground">Or tap <span className="font-semibold text-ink">Search</span> to find a product manually.</div>
          </div>
        )}
        {loading && (
          <div className="py-8 flex items-center justify-center gap-2 data-sku" data-testid="scan-loading">
            <Loader2 size={16} className="animate-spin" /> Loading product…
          </div>
        )}
        {current && !loading && (
          <ProductPanel
            product={current}
            form={form}
            setForm={setForm}
            onSubmit={submitAdd}
            onClose={() => { setCurrent(null); setScannedBarcode(null); setShelfLifeSuggestion(null); setQuantitySuggestion(null); setForm(DEFAULT_FORM); }}
            submitting={submitting}
            shelfLifeSuggestion={shelfLifeSuggestion}
            onSuggestionDismissed={() => setShelfLifeSuggestion(null)}
            quantitySuggestion={quantitySuggestion}
            onQuantitySuggestionDismissed={() => setQuantitySuggestion(null)}
          />
        )}
      </div>

      {sharedDiscovery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 px-4" data-testid="shared-catalog-dialog" role="dialog" aria-modal="true" aria-labelledby="shared-catalog-title">
          <div className="w-full max-w-md bg-white border-2 border-ink p-6">
            <div className="data-sku mb-1">Recognized public product</div>
            <h2 id="shared-catalog-title" className="font-display font-black text-xl mb-4">Add to your store catalog?</h2>
            <div className="flex items-center gap-3 bg-secondary border border-border p-3 mb-4">
              <ProductImage src={sharedDiscovery.product.image_url} alt={sharedDiscovery.product.name} size="w-12 h-12" />
              <div className="min-w-0">
                <div className="font-display font-semibold">{sharedDiscovery.product.name}</div>
                {sharedDiscovery.product.brand && <div className="text-sm text-muted-foreground">{sharedDiscovery.product.brand}</div>}
                <div className="data-sku">{sharedDiscovery.product.barcode}</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4">This product is recognized. Add it to your selected store and department before recording stock.</p>
            {sharedContextReady && <p className="text-sm mb-4" data-testid="shared-catalog-destination">{operationalContext?.storeName || user?.store_name || "Selected store"} · {deptLabel(deptNameMap[deptId]) || deptLabel(user?.department_name) || "Selected department"}</p>}
            {isDirty && <p className="text-sm mb-4" data-testid="shared-catalog-unsaved">Your current stock entry will stay open. You can choose whether to switch after adding this product.</p>}
            {!sharedContextReady && <p className="text-sm text-shelf-attention mb-4" data-testid="shared-catalog-context-required">Select a store and department in Floor Mode, then scan this product again.</p>}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={dismissSharedProduct} className="btn-secondary h-12" disabled={attachingShared} data-testid="shared-catalog-cancel">Back to scanner</button>
              <button type="button" onClick={attachSharedProduct} className="btn-primary h-12" disabled={attachingShared || !sharedContextReady} data-testid="shared-catalog-confirm">{attachingShared ? "Adding…" : "Add to store catalog"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- confirmation: replace product? ---------- */}
      {pendingProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 px-4" data-testid="confirm-replace-modal">
          <div className="w-full max-w-md bg-white border-2 border-ink p-6">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="text-shelf-urgent shrink-0" size={20} />
              <div>
                <div className="data-sku">Unsaved changes</div>
                <div className="font-display font-black text-xl leading-tight">Discard current product?</div>
              </div>
            </div>
            <div className="bg-secondary border border-border p-3 mb-4 flex items-center gap-3">
              <ProductImage src={pendingProduct.image_url} alt={pendingProduct.name} size="w-12 h-12" />
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold truncate text-sm">{pendingProduct.name}</div>
                <div className="data-sku truncate">{pendingProduct.barcode}</div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              You haven't saved <span className="font-bold text-ink">{current?.name}</span>. If you continue, your current quantity and expiration date will be lost.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={cancelPending} className="btn-secondary h-12" data-testid="confirm-replace-cancel">Keep current</button>
              <button onClick={acceptPending} className="btn-primary h-12" data-testid="confirm-replace-accept">Discard &amp; switch</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- search overlay ---------- */}
      {searchOpen && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-ink/60" data-testid="search-overlay">
          <div className="w-full sm:max-w-md bg-white border-t-4 sm:border-4 border-ink p-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-3 gap-2">
              <button
                type="button"
                onClick={closeSearch}
                className="h-11 px-3 -ml-1 inline-flex items-center gap-1.5 font-display uppercase text-sm tracking-wide text-ink active:scale-[0.98] transition-transform"
                data-testid="search-back-to-scanner-button"
              >
                <ArrowLeft size={18} strokeWidth={2.5} /> Back to Scanner
              </button>
              <div className="font-display font-black text-base">Search</div>
              {/* Keep the bare ✕ for one-handed thumb reach in the top-right. */}
              <button
                onClick={closeSearch}
                className="p-2 -mr-1"
                aria-label="Close search"
                data-testid="close-search-button"
              >
                <X size={20} />
              </button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => {
                  searchSessionRef.current += 1;
                  setQuery(e.target.value);
                  setSearchSelection(null);
                }}
                maxLength={120}
                placeholder="Name or barcode…"
                className="w-full h-12 pl-12 pr-4 bg-input border border-border rounded-sm font-body focus:outline-none focus:border-ink"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center justify-between mb-2 -mt-1 px-1">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink/50" data-testid="search-source-label">
                {isOnline() ? "Your catalog + shared catalog" : "On-device catalog only (offline)"}
              </span>
              <button
                type="button"
                onClick={refreshCatalogManually}
                className="font-mono text-[10px] uppercase tracking-wider text-ink/60 hover:text-ink underline-offset-2 hover:underline"
                data-testid="refresh-catalog-button"
              >
                Refresh catalog
              </button>
            </div>
            <div className="flex-1 overflow-y-auto -mx-1" data-testid="search-results">
              {searching && <div className="p-3 data-sku">Searching…</div>}
              {selectingSearch && <div className="p-3 data-sku" role="status">Checking product…</div>}
              {hasQuery && activeSearch?.ownedStatus === "error" && (
                <div className="p-3 text-sm" role="status" data-testid="owned-search-error">
                  Your catalog could not be searched.
                  <button type="button" className="ml-2 underline" onClick={() => setSearchRetry((value) => value + 1)}>Try again</button>
                </div>
              )}
              {!searching && !selectingSearch && results.length === 0 && sharedResults.length === 0 && hasQuery
                && activeSearch?.ownedStatus === "ready" && activeSearch?.sharedStatus === "ready"
                && !activeSearch.nextCursor && (
                <div className="p-4 text-center">
                  <div className="data-sku mb-2">No matches</div>
                  <button
                    className="btn-secondary h-11"
                    onClick={() => {
                      const seedBarcode = query.match(/^\d+$/) ? query : "";
                      setRequestSheet(seedBarcode || "");
                      setRecognizedRequest(false);
                      setRequestForm({ barcode: seedBarcode, name_guess: "", brand: "", notes: "" });
                      setExtractionSource(seedBarcode ? "manual" : null);
                      closeSearch();
                    }}
                    data-testid="request-new-product-button"
                  >
                    <Plus size={14} /> Request new product
                  </button>
                </div>
              )}
              {results.length > 0 && <h3 className="px-3 py-2 data-sku">Your catalog</h3>}
              {results.map((p) => (
                <button
                  key={p.id}
                  onClick={() => pickFromSearch(p)}
                  disabled={selectingSearch}
                  className="w-full px-3 py-2 hover:bg-secondary text-left flex items-center gap-3 border-b border-border last:border-b-0"
                  data-testid={`search-row-${p.id}`}
                >
                  <ProductImage src={p.image_url} alt={p.name} size="w-12 h-12" />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold truncate">{p.name}</div>
                    <div className="data-sku truncate">{p.barcode} · {p.category || "—"}</div>
                  </div>
                  <Plus className="text-brand" size={18} strokeWidth={2.5} />
                </button>
              ))}
              {hasQuery && (
                <section className="mt-3 border-t border-border pt-2" data-testid="shared-search-results">
                  <h3 className="px-3 py-2 data-sku">Shared catalog</h3>
                  {activeSearch?.sharedStatus === "loading" && <p className="px-3 pb-3 text-sm" role="status">Searching reviewed public products…</p>}
                  {activeSearch?.sharedStatus === "offline" && <p className="px-3 pb-3 text-sm">Connect to search the shared catalog.</p>}
                  {activeSearch?.sharedStatus === "error" && (
                    <p className="px-3 pb-3 text-sm" role="status" data-testid="shared-search-error">
                      Shared catalog is unavailable. Your search is incomplete.
                      <button type="button" className="ml-2 underline" onClick={() => setSearchRetry((value) => value + 1)}>Try again</button>
                    </p>
                  )}
                  {sharedResults.length > 0 && <p className="px-3 pb-2 text-xs text-muted-foreground">Reviewed public products. Add to your catalog before recording stock.</p>}
                  {sharedResults.map((p) => (
                    <button key={p.id} type="button" onClick={() => pickSharedFromSearch(p)} disabled={selectingSearch}
                      className="w-full px-3 py-2 hover:bg-secondary text-left flex items-center gap-3 border-b border-border"
                      data-testid={`shared-search-row-${p.id}`}>
                      <ProductImage src={p.image_url} alt={p.name} size="w-12 h-12" />
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold truncate">{p.name}</div>
                        {p.brand && <div className="text-sm text-muted-foreground truncate">{p.brand}</div>}
                        <div className="data-sku truncate">{p.barcode}</div>
                      </div>
                      <span className="text-xs font-semibold">Review</span>
                    </button>
                  ))}
                  {activeSearch?.moreError && <p className="px-3 py-2 text-sm" role="status">More shared results could not be loaded. Try again.</p>}
                  {activeSearch?.nextCursor && <button type="button" onClick={loadMoreShared}
                    disabled={activeSearch.loadingMore || selectingSearch} className="btn-secondary h-11 m-3" data-testid="shared-search-more">
                    {activeSearch.loadingMore ? "Loading…" : "Show more shared products"}
                  </button>}
                </section>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------- product request sheet ---------- */}
      {requestSheet !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/60 overflow-y-auto" data-testid="product-request-sheet">
          <div className="w-full sm:max-w-md bg-white border-t-4 sm:border-4 border-dashed border-brand p-6 max-h-[95vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4 gap-2">
              <button
                type="button"
                onClick={() => { setRequestSheet(null); setRequestPhotos([]); setExtractionSource(null); }}
                className="h-11 px-3 -ml-1 inline-flex items-center gap-1.5 font-display uppercase text-sm tracking-wide text-ink active:scale-[0.98] transition-transform"
                data-testid="request-back-to-scanner-button"
              >
                <ArrowLeft size={18} strokeWidth={2.5} /> Back to Scanner
              </button>
              <div className="text-right">
                <div className="data-sku flex items-center justify-end gap-1"><AlertTriangle size={12} /> {recognizedRequest ? "Recognized public product" : "Unknown product"}</div>
                <div className="font-display font-black text-lg">{recognizedRequest ? "Request catalog addition" : "Request new product"}</div>
              </div>
              <button
                onClick={() => { setRequestSheet(null); setRequestPhotos([]); setExtractionSource(null); }}
                className="p-2 -mr-1"
                aria-label="Close request sheet"
                data-testid="close-request-sheet"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <SmartCaptureBlock
                initialBarcode={requestForm.barcode}
                disabled={submitting}
                onApply={applyDetected}
              />
              <div>
                <label className="data-sku block mb-2">Barcode</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={requestForm.barcode}
                  onChange={(e) => {
                    setRequestForm({ ...requestForm, barcode: e.target.value });
                    // Manual edits demote the audit source unless we had nothing.
                    setExtractionSource((prev) => (prev && prev !== "manual" ? "mixed" : "manual"));
                  }}
                  className="w-full h-14 px-4 border border-border rounded-sm bg-input font-mono"
                  placeholder="Type or scan a barcode"
                  data-testid="request-barcode-input"
                />
              </div>
              <div>
                <label className="data-sku block mb-2">{recognizedRequest ? "Product name" : "Product name (guess)"}</label>
                <input
                  type="text"
                  value={requestForm.name_guess}
                  onChange={(e) => setRequestForm({ ...requestForm, name_guess: e.target.value })}
                  className="w-full h-14 px-4 border border-border rounded-sm bg-input"
                  placeholder="e.g. Brie de Meaux 250g"
                  data-testid="request-name-input"
                />
              </div>
              <div>
                <label className="data-sku block mb-2">Brand (optional)</label>
                <input
                  type="text"
                  value={requestForm.brand}
                  onChange={(e) => setRequestForm({ ...requestForm, brand: e.target.value })}
                  className="w-full h-14 px-4 border border-border rounded-sm bg-input"
                  placeholder="e.g. President"
                  data-testid="request-brand-input"
                />
              </div>
              <div>
                <label className="data-sku block mb-2">Notes</label>
                <textarea
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                  rows={3}
                  className="w-full p-4 border border-border rounded-sm bg-input"
                  placeholder="Anything the admin should know"
                  data-testid="request-notes-input"
                />
              </div>
              <PhotoCaptureField
                value={requestPhotos}
                onChange={setRequestPhotos}
                max={3}
                disabled={submitting}
                onExtractFromImage={async (dataUrl) => {
                  // "Take photo" attachments ALSO run smart-capture extraction
                  // so the user never has to figure out which of two camera
                  // CTAs is the OCR one. Same helper, same toasts.
                  await extractProductFromImage(dataUrl, applyDetected);
                }}
              />
              {operationalContext?.requireDepartmentForRequest && !deptId && (
                <p className="text-xs text-status-urgent" data-testid="hq-scan-request-department-required">
                  Select a department in Floor Mode before submitting this request.
                </p>
              )}
              <button
                className="btn-primary w-full"
                onClick={submitRequest}
                disabled={
                  submitting
                  || !(requestForm.barcode || "").trim()
                  || (operationalContext?.requireDepartmentForRequest && !deptId)
                }
                data-testid="submit-request-button"
              >
                {submitting ? "Sending…" : "Submit for admin review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === Product panel (always at the bottom of the scan page) ===
function ProductPanel({ product, form, setForm, onSubmit, onClose, submitting, shelfLifeSuggestion, onSuggestionDismissed, quantitySuggestion, onQuantitySuggestionDismissed }) {
  const isSuggestionActive =
    !!shelfLifeSuggestion && form.expiration_date === shelfLifeSuggestion.suggested_date;
  const isQuantitySuggestionActive =
    !!quantitySuggestion && Number(form.quantity) === Number(quantitySuggestion.suggested_quantity);
  const setExpiration = (next) => {
    setForm((f) => ({ ...f, expiration_date: next }));
    if (onSuggestionDismissed) onSuggestionDismissed();
  };
  const setQuantity = (next) => {
    setForm((f) => ({ ...f, quantity: next }));
    if (onQuantitySuggestionDismissed) onQuantitySuggestionDismissed();
  };
  return (
    <div data-testid="product-panel">
      <div className="flex items-start gap-3 mb-3">
        <ProductImage src={product.image_url} alt={product.name} size="w-16 h-16" />
        <div className="min-w-0 flex-1">
          <div className="data-sku">Loaded</div>
          <div className="font-display font-black text-xl leading-tight truncate">{product.name}</div>
          <div className="data-sku truncate">{product.barcode} · {product.category || "—"}</div>
        </div>
        <button onClick={onClose} className="p-1" data-testid="panel-close">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="data-sku block mb-1">Quantity</label>
          <div className="flex items-stretch">
            <button
              type="button"
              className="h-12 w-12 border-2 border-ink font-display font-bold text-xl active:bg-secondary"
              onClick={() => setQuantity(Math.max(1, Number(form.quantity) - 1))}
              data-testid="qty-minus"
              aria-label="Decrease quantity"
            >−</button>
            <input
              type="number"
              min={1}
              inputMode="numeric"
              value={form.quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-16 h-12 px-1 text-center font-mono text-lg border-y-2 border-ink bg-input"
              data-testid="add-quantity-input"
              aria-label="Quantity"
            />
            <button
              type="button"
              className="h-12 w-12 border-2 border-ink font-display font-bold text-xl active:bg-secondary"
              onClick={() => setQuantity(Number(form.quantity) + 1)}
              data-testid="qty-plus"
              aria-label="Increase quantity"
            >+</button>
          </div>
          {isQuantitySuggestionActive && (
            <div
              className="mt-1 text-[11px] font-mono text-ink/70 flex items-center gap-1"
              data-testid="quantity-suggestion-hint"
            >
              <Info size={11} />
              Suggested from previous entry ({quantitySuggestion.suggested_quantity})
            </div>
          )}
        </div>
        <div>
          <label className="data-sku block mb-1">Add expiration</label>
          <input
            type="date"
            value={form.expiration_date}
            min={todayPlus(-30)}
            onChange={(e) => setExpiration(e.target.value)}
            className="w-full h-12 px-3 border border-border rounded-sm bg-input font-mono text-sm"
            data-testid="add-expiration-input"
          />
          {isSuggestionActive && (
            <div
              className="mt-1 text-[11px] font-mono text-ink/70 flex items-center gap-1"
              data-testid="shelf-life-suggestion-hint"
            >
              <Info size={11} />
              Suggested from previous entry ({shelfLifeSuggestion.suggested_days}d)
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1.5 mb-3 overflow-x-auto no-scrollbar">
        {[5, 7, 10, 15, 30].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setExpiration(todayPlus(n))}
            className="shrink-0 h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-wider rounded-sm hover:bg-secondary active:bg-secondary"
            data-testid={`quick-shelf-life-${n}d`}
          >
            +{n}d
          </button>
        ))}
      </div>
      <button className="btn-primary w-full h-14" onClick={onSubmit} disabled={submitting} data-testid="confirm-add-button">
        <Check size={18} /> {submitting ? "Saving…" : `Save ${form.quantity} × ${product.name.length > 18 ? product.name.slice(0, 18) + "…" : product.name}`}
      </button>
    </div>
  );
}
